import { faroCoreApiClient } from "@api/faro-core-api-client";
import { getCachedTokenProvider } from "@api/use-cached-token-provider";
import { exponentialBackOff, retry } from "@faro-lotv/foundation";
import { FILE_SIZE_MULTIPLIER, isValidFileExtension } from "@utils/file-utils";
import SparkMD5 from "spark-md5";

/** Higher numbers starting from this considers error code */
const SMALLEST_ERROR_CODE = 300;

/**
 * Maximum file name length.
 * This is restriction set by the Project API where mutations to add files fail if the name is longer than 200
 */
const MAX_LENGTH_NAME = 200;

/**
 * Regular expression used to detect potentially malicious code patterns in SVG files.
 *
 * It checks for:
 * - `<script>` tags (inline scripts)
 * - Inline event handlers (e.g., `onload=`, `onclick=`)
 * - JavaScript URLs inside href or xlink:href attributes
 */
const MALICIOUS_CODE = /<script\b|(^|[\s"'<])on\w+\s*=|xlink:href\s*=\s*["']\s*javascript:|href\s*=\s*["']\s*javascript:/i;
/**
 * Upload a single chunk to the backend
 *
 * @param fileSize Total size of the file
 * @param fileType The MIME type of the file
 * @param chunk Single chunk request data
 * @param blobData A single chunk as ArrayBuffer to upload
 * @param bytesUploaded Total file size already uploaded
 * @param onUploadProgress Callback function to show progress
 * @returns boolean True if the chunk uploaded successfully, otherwise false
 */
async function uploadChunk(fileSize, fileType, chunk, blobData, bytesUploaded, onUploadProgress) {
  // Upload the chunk with retries. Currently only network errors are retried.
  const response = await retry(() => faroCoreApiClient.file.uploadAsset({
    method: chunk.method,
    uploadUrl: chunk.url,
    file: blobData,
    type: fileType,
    headers: {
      ...chunk.headers
    },
    onUploadProgress: event => {
      onProgress(event, fileSize, bytesUploaded, onUploadProgress);
    }
  }), {
    max: 5,
    delay: exponentialBackOff
  });

  // handle error
  if (!response.isUploaded || response.status >= SMALLEST_ERROR_CODE) {
    throw new Error(response.statusText);
  }
  return true;
}

/**
 * Calculates the total upload progress while chunk uploading
 *
 * @param event The progress event of a chunk upload
 * @param fileSize Total file size
 * @param bytesUploaded Total file size that already uploaded
 * @param onUploadProgress Call back function to show the progress
 */
function onProgress(event, fileSize, bytesUploaded, onUploadProgress) {
  const totalProgress = (bytesUploaded + event.loaded) / fileSize * 100;
  onUploadProgress(totalProgress);
}

/**
 * Finalize the chunk upload to the backend
 *
 * @param chunkUploadDescription the descriptor for the entire upload
 * @param spark Array buffer to compute entire file MD5
 * @param finalizer Function to commit the chunk upload
 */
async function finalizeUpload(chunkUploadDescription, spark) {
  const {
    finalize,
    downloadUrl
  } = chunkUploadDescription;

  // Finalize chunk upload
  const finalizeResponse = await fetch(finalize.url, {
    method: finalize.method,
    headers: {
      ...finalize.headers
    },
    body: finalize.body
  });

  // check error
  if (!finalizeResponse.ok || finalizeResponse.status >= SMALLEST_ERROR_CODE) {
    throw new Error(finalizeResponse.statusText);
  }
  const md5 = spark.end();
  return {
    downloadUrl,
    md5
  };
}

/**
 * Performs the chunk uploading.
 */
export async function doUpload({
  file,
  projectId,
  companyId,
  memberId,
  onUploadProgress
}) {
  try {
    const tokenProvider = getCachedTokenProvider(projectId);
    const token = await tokenProvider();

    // Get description on how to split the chunked upload.
    const chunkUploadDescription = await faroCoreApiClient.file.generateChunkUploadData({
      projectId,
      companyId,
      memberId,
      payload: {
        contentType: file.type,
        downloadName: file.name,
        size: file.size,
        token
      }
    });

    // initialize MD5 computation
    const spark = new SparkMD5.ArrayBuffer();
    let bytesUploaded = 0;

    // progressively upload all file chunks
    for (const chunk of chunkUploadDescription.chunks) {
      const startByte = chunk.bytes.start;
      const endByte = chunk.bytes.start + chunk.bytes.length;
      const blob = file.slice(startByte, endByte);
      const blobData = await blob.arrayBuffer();
      spark.append(blobData);
      if (!(await uploadChunk(file.size, file.type, chunk, blobData, bytesUploaded, onUploadProgress))) {
        throw new Error("Upload failed because of an unknown error.");
      }
      bytesUploaded += chunk.bytes.length;
    }
    return await finalizeUpload(chunkUploadDescription, spark);
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    } else {
      throw new Error("Upload failed because of an unknown error.", {
        cause: error
      });
    }
  }
}

/**
 * Validates a file based on the params
 *
 * @returns ValidateFileResponse based on the validity of the file
 */
export function validateFile({
  file,
  allowedExtensions,
  maxFileSize
}) {
  if (!isValidFileExtension(file, allowedExtensions)) {
    return {
      isValid: false,
      message: "Invalid file type",
      description: "The selected file is not valid type to upload."
    };
  }
  if (maxFileSize && file.size > maxFileSize * FILE_SIZE_MULTIPLIER * FILE_SIZE_MULTIPLIER) {
    return {
      isValid: false,
      message: "File size exceeds the maximum limit",
      description: `Please select a file with a size not exceeding ${maxFileSize} MB.`
    };
  }
  if (file.name.length > MAX_LENGTH_NAME) {
    return {
      isValid: false,
      message: "File name is too long",
      description: "The file name must have a maximum length of 200 characters."
    };
  }
  return {
    isValid: true
  };
}

/** Checks if an uploaded SVG file contains potentially malicious code. */
export async function isMaliciousSvgFile(file) {
  if (!file || file.type !== "image/svg+xml") {
    return false;
  }
  const svgText = await file.text();
  const hasMaliciousCode = MALICIOUS_CODE.test(svgText);
  return hasMaliciousCode;
}